import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CCard, CCardBody } from '@coreui/react-pro';
import { CART_TYPES, LOCAL_CART, LOCAL_COUPON, LOCAL_COUPON_VOUCHERS, LOCAL_ORG_USER, MODAL_LOGIN, POPUP_TYPE } from '../../../../constants';
import NoYesPopup from '../../../General/Popups/NoYesPopup';
import SelectOrCreateOrganization from './SelectOrCreateOrganization';
import { isOrgUser } from '../../../../utils';
import { setAfterLogin, setShow } from '../../../../actions/general';
import { fetchManagedOrganizationsRequest } from '../../Organizations/ManagedOrganizations/actions';
import CreateOrganization from './CreateOrganization';
import SelectOrganization from './SelectOrganization';
import { fetchGuestContentManagements } from '../../../../containers/guest/action';
import { actionClearCartCouponVouchers } from '../Cart/actions';

const SwitchPurchase = ({ handleSelectOrganization, handleSwitchIndividual }) => {
     const dispatch = useDispatch();
     const show = useSelector((state) => state.general.show);
     const user = useSelector((state) => state.subscriber.user);
     const managedOrganizations = useSelector((state) => state.subscriber.managedOrganizations);
     const cartLocalStorage = JSON.parse(localStorage.getItem(LOCAL_CART));
     const isCartOrg = cartLocalStorage?.type === CART_TYPES.ORGANIZATION;
     const orgUserLocal = JSON.parse(localStorage.getItem(LOCAL_ORG_USER));
     const showOrgDefault = user ? isOrgUser() : isCartOrg;

     const switchToOrgButtonText = {
          noButtonText: 'No, Go Back',
          yesButtonText: `Yes, Switch to Organization Purchase & Clear My Cart`,
     };
     const switchToIndividualButtonText = {
          noButtonText: 'No, Go Back',
          yesButtonText: `Yes, Switch to Personal Purchase & Clear My Cart`,
     };

     const [showOrg, setShowOrg] = useState(showOrgDefault);
     const [buttonTextPopup, setButtonTextPopup] = useState(switchToOrgButtonText);

     const fetchManagedOrganizations = () => {
          if (!managedOrganizations) {
               if (user) {
                    dispatch(fetchManagedOrganizationsRequest());
               }
          }
     };
     useEffect(fetchManagedOrganizations, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          if (showOrg) {
               setButtonTextPopup(switchToIndividualButtonText);
          } else {
               setButtonTextPopup(switchToOrgButtonText);
          }
          setShowOrg(showOrgDefault);
     }, [showOrg, isOrgUser(), isCartOrg]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleShowSwitchPurchasePopup = () => {
          dispatch(setShow(POPUP_TYPE.SWITCH_PURCHASE));
     };

     const UserChildrenContent = () => {
          return (
               <>
                    <p>Your current shopping cart is set up to place a Personal Training purchase for your own training.</p>
                    <p>
                         If you choose to switch to an Organization purchase we will clear your cart and take you back to the training products page.
                    </p>
               </>
          );
     };

     const OrgChildrenContent = () => {
          return (
               <>
                    <p>
                         Your current shopping cart is set up to place a purchase on behalf of{' '}
                         {orgUserLocal && <strong>{orgUserLocal.organization?.name}</strong>}.
                    </p>
                    <p>
                         If you choose to place a Personal Training purchase instead of an Organization purchase, we will clear your cart and take you
                         back to the training products page.
                    </p>
               </>
          );
     };

     const OrgPromo = () => {
          return (
               <div className="org-promo">
                    <p className="m-0">
                         Organization Purchase: You are viewing multi-seat training products intended for Organizations. Your purchase will be placed
                         on behalf of an Organization, and you'll be able to distribute training seats to individuals. If you want to purchase
                         individually, personal training seats for yourself, switch to a Personal Training purchase.
                    </p>
                    {user && (
                         <div className="mt-3 org-promo__button">
                              <CButton className="me-3 btn-blue-dark btn-round-9 btn-h-50" onClick={handleSwitchToOrgButtonClick}>
                                   Change to a Different Organization
                              </CButton>
                              <CButton className="btn-blue-highlight btn-round-9 btn-h-50" onClick={handleShowSwitchPurchasePopup}>
                                   Switch to a Personal purchase
                              </CButton>
                         </div>
                    )}
               </div>
          );
     };

     const IndividualPromo = () => {
          const dispatch = useDispatch();
          const contentManagements = useSelector((state) => state.subscriber.contentManagements);

          const fetchDataContentManagements = () => {
               if (!contentManagements.length) {
                    dispatch(fetchGuestContentManagements());
               }
          };

          useEffect(fetchDataContentManagements, []); // eslint-disable-line react-hooks/exhaustive-deps
          const contentIndividual = contentManagements.find((content) => content.key === 'pageSellingGroupIndividualPromo');
          return (
               <div className="individual-promo">
                    <p className="m-0">{contentIndividual?.value}</p>
                    {user && (
                         <div className="mt-3">
                              <CButton className="btn-blue-highlight btn-h-50 btn-round-9" onClick={handleShowSwitchPurchasePopup}>
                                   Switch to an Organization Purchase
                              </CButton>
                         </div>
                    )}
               </div>
          );
     };

     const handleSwitchToOrgButtonClick = () => {
          if (user) {
               dispatch(setShow(POPUP_TYPE.SELECT_OR_CREATE_ORG));
          } else {
               dispatch(setShow(MODAL_LOGIN));
               dispatch(setAfterLogin(POPUP_TYPE.SELECT_OR_CREATE_ORG));
          }
     };

     const handleSwitchToIndividualButtonClick = () => {
          handleSwitchIndividual();
          dispatch(setShow(null));

          localStorage.removeItem(LOCAL_CART);
          localStorage.removeItem(LOCAL_COUPON);
          localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
          dispatch(actionClearCartCouponVouchers());
     };

     const handleSwitchButtonClick = () => {
          if (!user) {
               dispatch(setShow(MODAL_LOGIN));

               if (!showOrg) {
                    dispatch(setAfterLogin(POPUP_TYPE.SELECT_OR_CREATE_ORG));
               }

               return;
          }

          if (showOrg) {
               handleSwitchToIndividualButtonClick();
          } else {
               handleSwitchToOrgButtonClick();
          }
     };

     return (
          <>
               {user && (
                    <>
                         {show === POPUP_TYPE.SWITCH_PURCHASE && (
                              <NoYesPopup
                                   className={`popup-custom switchTo${!showOrg ? ' switchToOrg' : ''}`}
                                   classButtonYes={'btn-h-50 btn-round-9 btn-blue-highlight'}
                                   classButtonNo={'btn-h-50 btn-round-9'}
                                   title="Are you sure you want to switch?"
                                   noButtonText={buttonTextPopup.noButtonText}
                                   yesButtonText={buttonTextPopup.yesButtonText}
                                   showPopup
                                   onClose={() => dispatch(setShow(null))}
                                   onAccept={handleSwitchButtonClick}
                              >
                                   {showOrg ? <OrgChildrenContent /> : <UserChildrenContent />}
                              </NoYesPopup>
                         )}
                    </>
               )}

               {show === POPUP_TYPE.SELECT_OR_CREATE_ORG && <SelectOrCreateOrganization handleSelectOrganization={handleSelectOrganization} />}
               {show === POPUP_TYPE.CREATE_ORG && <CreateOrganization />}
               {show === POPUP_TYPE.SELECT_ORG && <SelectOrganization handleSelectOrganization={handleSelectOrganization} />}

               {cartLocalStorage && (
                    <CCard className="selling-group__promo">
                         <CCardBody>{showOrg ? <OrgPromo /> : <IndividualPromo />}</CCardBody>
                    </CCard>
               )}
          </>
     );
};

export default SwitchPurchase;
